import FetchClient from "./FetchClient";
import { getTrackerLimit } from "./TrackingService";

export interface ExtraStore {
  storeId: string;
  inventory: number;
  storePrice: number;
}

export interface HomeDepotTrackedProduct {
  userId: string;
  upc: string; // Sort key (for Home Depot, this contains the ItemID)
  itemId: string; // Home Depot Item ID (same as upc field for Home Depot)
  actualUpc?: string; // The actual UPC from product data
  name: string;
  image: string;
  category: string;
  storeId: string;
  targetPrice: number;
  currentPrice: number;
  msrp: number;
  active: boolean;
  createdAt: number;
  lastChecked: number;
  lastNotified?: number;
  url?: string;
  prices: number[];
  checkTimestamps: number[];
  currentStock: number;
  stockStatus: string;
  stockHistory: number[];
  stockCheckTimestamps: number[];
  available: number;
}

export interface AddHomeDepotTrackerRequest {
  itemId: string; // Home Depot Item ID (primary identifier)
  upc?: string; // Optional - actual UPC from product data
  name: string;
  image: string;
  category: string;
  zipCode: string; // Changed from storeId to zipCode
  storeId?: string; // Optional - will be determined from zipCode if not provided
  targetPrice: number;
  currentPrice: number;
  msrp: number;
  url?: string;
  currentStock?: number;
  stockStatus?: string;
  extraStores?: ExtraStore[];
}

export interface UpdateHomeDepotTrackerRequest {
  targetPrice?: number;
  active?: boolean;
}

export interface HomeDepotTrackingResponse {
  monitors: HomeDepotTrackedProduct[];
  total: number;
}

export interface ApiResponse<T> {
  data: T;
  status: number;
}

// Get all tracked Home Depot products for the current user
export async function getHomeDepotTrackedProducts(): Promise<
  HomeDepotTrackedProduct[]
> {
  try {
    const response = await FetchClient.get<
      ApiResponse<HomeDepotTrackingResponse>
    >("/monitors-homedepot");
    return response.data.monitors;
  } catch (error) {
    console.error("Error fetching Home Depot tracked products:", error);
    throw error;
  }
}

// Add a new Home Depot product to tracking
export async function addHomeDepotTracker(
  trackerData: AddHomeDepotTrackerRequest
): Promise<HomeDepotTrackedProduct> {
  try {
    // Check current tracker count before adding
    const currentTrackers = await getHomeDepotTrackedProducts();

    const limit = getTrackerLimit();
    if (currentTrackers.length >= limit) {
      throw new Error(
        `You can only track up to ${limit} Home Depot products. Please remove some trackers to add new ones.`
      );
    }

    const response = await FetchClient.post<
      ApiResponse<HomeDepotTrackedProduct>
    >("/monitors-homedepot", trackerData);
    return response.data;
  } catch (error) {
    console.error("Error adding Home Depot tracker:", error);
    throw error;
  }
}

// Update an existing Home Depot tracker
export async function updateHomeDepotTracker(
  trackerId: string,
  updateData: UpdateHomeDepotTrackerRequest
): Promise<HomeDepotTrackedProduct> {
  try {
    const response = await FetchClient.patch<
      ApiResponse<HomeDepotTrackedProduct>
    >(`/monitors-homedepot/${trackerId}`, updateData);
    return response.data;
  } catch (error) {
    console.error("Error updating Home Depot tracker:", error);
    throw error;
  }
}

// Remove a Home Depot tracker
export async function removeHomeDepotTracker(upc: string): Promise<void> {
  try {
    await FetchClient.delete<ApiResponse<null>>(`/monitors-homedepot/${upc}`);
  } catch (error) {
    console.error("Error removing Home Depot tracker:", error);
    throw error;
  }
}

// Check if a Home Depot product is being tracked
export async function isHomeDepotProductTracked(upc: string): Promise<boolean> {
  try {
    const response = await FetchClient.get<ApiResponse<{ monitored: boolean }>>(
      `/monitors-homedepot/check/${upc}`
    );
    return response.data.monitored;
  } catch (error) {
    console.error("Error checking if Home Depot product is tracked:", error);
    return false;
  }
}

// Get Home Depot tracker details for a specific product
export async function getHomeDepotTrackerByUpc(
  upc: string
): Promise<HomeDepotTrackedProduct | null> {
  try {
    const response = await FetchClient.get<
      ApiResponse<HomeDepotTrackedProduct>
    >(`/monitors-homedepot/product/${upc}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching Home Depot tracker by UPC:", error);
    return null;
  }
}

// Toggle Home Depot tracker active status
export async function toggleHomeDepotTrackerStatus(
  trackerId: string,
  active: boolean
): Promise<HomeDepotTrackedProduct> {
  return updateHomeDepotTracker(trackerId, { active });
}

// Update Home Depot tracker target price
export async function updateHomeDepotTrackerPrice(
  trackerId: string,
  targetPrice: number
): Promise<HomeDepotTrackedProduct> {
  return updateHomeDepotTracker(trackerId, { targetPrice });
}

// Manually update a Home Depot tracker with fresh product data
export async function manuallyUpdateHomeDepotTracker(
  upc: string
): Promise<HomeDepotTrackedProduct> {
  try {
    const response = await FetchClient.post<
      ApiResponse<HomeDepotTrackedProduct>
    >(`/monitors-homedepot/manually-update/${upc}`, {});
    return response.data;
  } catch (error) {
    console.error("Error manually updating Home Depot tracker:", error);
    throw error;
  }
}
